import React, { Component, Fragment } from "react";
import * as _ from "lodash";
import { Dictionary } from "lodash";
import { FontSizes, Label, Link } from "office-ui-fabric-react";

const pillColours: Dictionary<string> = {
    "ConstantNamePart": "black",
    "MisItemNamePart": "blue",
    "SdsRowNamePart": "amber",
    "ConfigNamePart": "green"
};

type PartType = "available" | "actual";

interface Part {
    type: PartType;
    partName: string;
    friendlyName: string;
    groupName: string;
}

export interface NameBuilderProps {
    title: string;

    actualPartNames: string[];
    defaultNameParts?: string[];
    availablePartNames: string[];
    onChange(newParts: string[]): void;
}

export interface NameBuilderState {
    editing: boolean;
    availableParts: Part[];
    actualParts: Part[]
    partBeingDragged?: Part;
}

export class NameBuilder extends Component<NameBuilderProps, NameBuilderState> {

    constructor(props: NameBuilderProps) {
        super(props);

        this.state = {
            editing: false,
            availableParts: [],
            actualParts: [],
            partBeingDragged: undefined
        };
    }

    public async componentDidMount() {
        if (this.props.actualPartNames.length < 1 && this.props.defaultNameParts != null) {
            this.props.onChange(this.props.defaultNameParts);
        }
        else {
            this.setPartsFromNames();
        }
    }

    public componentDidUpdate(prevProps: Readonly<NameBuilderProps>, prevState: Readonly<NameBuilderState>, snapshot?: any) {
        if (this.props.actualPartNames !== prevProps.actualPartNames || this.props.availablePartNames !== prevProps.availablePartNames) {
            this.setPartsFromNames();
        }
    }

    private setPartsFromNames() {
        this.setState({
            availableParts: this.props.availablePartNames.map(n => this.buildPart(n, "available")),
            actualParts: this.props.actualPartNames.map(name => this.buildPart(name, "actual"))
        });
    }

    private buildPart(partName: string, partType: PartType): Part {
        const splitPart = _.split(partName, "+");
        return {
            type: partType,
            partName: partName,
            friendlyName: splitPart[1],
            groupName: splitPart[0],
        }
    }

    private buildPill(part: Part, key: number): JSX.Element {
        return (
            <span key={key} className={`pill pill--width-fit-content pill--small pill--${pillColours[part.groupName]} m-025rem`}
                draggable={this.state.editing}
                onDragStart={() => {
                    this.setState({
                        partBeingDragged: part
                    });
                }}
                onDrop={(e) => {
                    this.handlePartDrop(this.state.partBeingDragged!, part);

                    e.preventDefault();
                    e.stopPropagation();
                }}
                onDragEnter={(e) => e.preventDefault()}
                onDragOver={(e) => e.preventDefault()}>
                {part.friendlyName}
            </span>
        );
    }

    private handlePartDrop = (partBeingDragged: Part, partDroppedOnTo: Part) => {
        if (partBeingDragged.type === "actual" && partDroppedOnTo.type === "available") {
            const newPartNames = _.without(this.state.actualParts, partBeingDragged).map(p => p.partName);
            this.props.onChange(newPartNames);
            return;
        }

        if (partBeingDragged.type === "actual" && partDroppedOnTo.type === "actual") {
            const indexDragged = this.state.actualParts.indexOf(partBeingDragged);
            const indexDroppedOnTo = this.state.actualParts.indexOf(partDroppedOnTo);

            if (Math.abs(indexDragged - indexDroppedOnTo) === 1) {
                const newParts = [...this.state.actualParts];
                newParts.splice(indexDragged, 1, partDroppedOnTo);
                newParts.splice(indexDroppedOnTo, 1, partBeingDragged);

                this.props.onChange(newParts.map(p => p.partName));
            }
            else {
                const direction = indexDroppedOnTo > indexDragged ? 0 : 1;
                const newParts = _.without(this.state.actualParts, partBeingDragged);
                newParts.splice(newParts.indexOf(partDroppedOnTo) + direction, 0, partBeingDragged);
                this.props.onChange(newParts.map(p => p.partName));
            }

            return;
        }

        if (partBeingDragged.type === "available" && partDroppedOnTo.type === "actual") {
            const indexActual = this.state.actualParts.indexOf(partDroppedOnTo);
            const newParts = [...this.state.actualParts];
            newParts.splice(indexActual, 0, partBeingDragged);
            this.props.onChange(newParts.map(p => p.partName));
        }
    }

    private handlePartsAreaDrop = (partBeingDragged: Part) => {
        if (this.state.editing === false) {
            return;
        }

        if (this.props.actualPartNames.length === 0) {
            this.props.onChange([this.state.partBeingDragged!.partName])
            return;
        }

        if (this.state.actualParts.includes(partBeingDragged)) {
            const without = _.without(this.state.actualParts, partBeingDragged);
            const newParts = [...without, partBeingDragged];
            this.props.onChange(newParts.map(p => p.partName));
        }
        else {
            const newParts = [...this.state.actualParts, partBeingDragged];
            this.props.onChange(newParts.map(p => p.partName));
        }
    }

    private handleAvailablePartsAreaDrop = (partBeingDragged: Part) => {
        if (partBeingDragged.type === "actual") {
            const newPartNames = _.without(this.state.actualParts, partBeingDragged).map(p => p.partName);
            this.props.onChange(newPartNames);
            return;
        }
    }

    private renderAvailablePartsArea(): JSX.Element {
        const groupedAvailableParts = _.groupBy(this.state.availableParts, p => p.groupName)

        const availablePartPillGroups = Object.keys(groupedAvailableParts).map(groupName => {
            return (
                <div key={groupName} className={"flex-row flex--flow-wrap"}>
                    {groupedAvailableParts[groupName].map((part, i) => this.buildPill(part, i))}
                </div>
            );
        });

        return (
            <div className={"name-builder__available-parts"}
                onDragEnter={(e) => e.preventDefault()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                    this.handleAvailablePartsAreaDrop(this.state.partBeingDragged!);
                }}>
                <Label>Available Parts (drag from here)</Label>
                {availablePartPillGroups}
            </div>
        );
    }

    private renderToggleEditingLink(): JSX.Element {
        const text = this.state.editing ? "Finish editing" : "Edit";

        return (
            <Link style={{ fontSize: FontSizes.small }} onClick={() => {
                this.setState({
                    editing: !this.state.editing
                })
            }}>
                {text}
            </Link>
        )
    }

    public render() {
        return (
            <Fragment>
                <div className={`name-builder ${this.state.editing ? "name-builder--editing" : null}`}>
                    {this.state.editing ? this.renderAvailablePartsArea() : null}

                    <Label>{this.props.title} {this.state.editing ? "(drop here)" : null}</Label>
                    <div className={"name-builder__parts"}
                        onDragEnter={(e) => e.preventDefault()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            this.handlePartsAreaDrop(this.state.partBeingDragged!);
                        }
                        }>
                        {this.state.actualParts.map((p, i) => this.buildPill(p, i))}
                    </div>
                    {this.renderToggleEditingLink()}

                </div>

            </Fragment>
        );
    }
}